<template>
  <div class="flex flex-col gap-5 min-h-screen p-4">
    <!-- Header Section -->
    <div
      class="relative flex flex-col w-full h-min bg-white rounded-lg p-4 gap-5 dark:bg-gray-800 dark:text-gray-200 shadow"
    >
      <nav
        class="w-full flex flex-row portrait:flex-col justify-between portrait:gap-5"
      >
        <div
          class="w-full flex flex-row gap-5 items-center justify-between landscape:mr-5"
        >
          <div class="w-fit flex flex-row gap-5 items-center justify-start">
            <button
              @click="goback"
              class="p-3 flex items-center justify-center aspect-square w-fit bg-blue-gray-500/10 hover:bg-blue-gray-500/20 rounded-lg dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors"
            >
              <i
                class="fa-solid fa-arrow-left text-base dark:text-gray-200"
              ></i>
            </button>
            <h3 class="text-xl font-medium dark:text-gray-200">Promotion</h3>
          </div>
          <button
            @click="showSettings"
            :class="[
              'relative p-3 flex items-center justify-center aspect-square w-fit rounded-lg transition-colors',
              defaultDataEmpty
                ? 'bg-red-500 text-white shake'
                : 'bg-blue-gray-500/10 hover:bg-blue-gray-500/20 dark:bg-gray-700 dark:hover:bg-gray-600',
            ]"
          >
            <i class="fa-solid fa-gear text-base"></i>

            <!-- Notification dot if data missing -->
            <span
              v-if="defaultDataEmpty"
              class="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-bold text-white bg-red-500 rounded-full animate-pulse"
            >
              !
            </span>
          </button>
        </div>

        <a
          href="#"
          class="w-full landscape:max-w-fit flex gap-2 items-center justify-center px-5 py-3 bg-blue-gray-500/10 rounded-lg text-sm font-medium h-min dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-gray-200 transition-all hover:shadow-md"
        >
          <i class="fa-regular fa-circle-play"></i>
          <span>Watch Video Tutorial</span>
        </a>
      </nav>
    </div>
    <AdSettings
      @close-settings="showSettings"
      :products="products"
      :currencySymbol="currencySymbol"
      :defaultData="defaultData"
      v-if="showAdSettings"
    />
    <div class="flex flex-col gap-5" v-else>
      <!-- Main Content -->
      <FacebookMarketing
        @save-facebook-settings="saveSettings"
        @open-default-store-data="showSettings"
        :isDefault="defaultDataEmpty"
      />
      <WhatsAppMarketing
        @save-whatsapp-settings="saveSettings"
        :isDefault="defaultDataEmpty"
      />
      <EmailMarketing
        @save-email-settings="saveSettings"
        :isDefault="defaultDataEmpty"
      />
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import FacebookMarketing from "@/components/facebookMarketing.vue";
import WhatsAppMarketing from "@/components/whatsAppMarketing.vue";
import EmailMarketing from "@/components/emailMarketing.vue";
import AdSettings from "@/components/adSettingsComponent.vue";
export default {
  name: "FacebookAdsSetup",
  data() {
    return {
      showVideoModal: false,
      videoURL: "https://www.youtube.com/watch?v=nel2b105sxE",
      refreshKey: this.$store.state.storeUpdatedAt || Date.now(),
      emailAutoPilotEnabled: false,
      whatsappAutoPilotEnabled: false,
      whatsappExpanded: false,
      emailExpanded: false,
      loading: false,
      saving: false,
      saveSuccess: false,
      showAdSettings: false,
      products: [],
      currencies: [],
      defaultData: {},
      adAccountInfo: {},
    };
  },
  components: {
    FacebookMarketing,
    WhatsAppMarketing,
    EmailMarketing,
    AdSettings,
  },
  mounted() {
    this.fetchProducts();
    this.getCurrency();
    this.getDefaultData();
  },
  computed: {
    defaultDataEmpty() {
      return !this.defaultData || Object.keys(this.defaultData).length === 0;
    },
    storeCurrencyId() {
      return this.$store.state.selectedStore.default_currency;
    },
    currencySymbol() {
      const currency = this.currencies.find(
        (currency) => currency.id === this.storeCurrencyId
      );
      return currency ? currency.currency_symbol : null;
    },
  },
  watch: {
    "$store.state.storeUpdatedAt": {
      immediate: true,
      handler(val) {
        this.refreshKey = val;
        this.fetchProducts();
        this.getCurrency();
        this.getDefaultData();
      },
    },
  },
  methods: {
    ...mapActions([
      "saveFacebookIntegration",
      "getProducts",
      "fetchCurrency",
      "getDefault",
    ]),
    async getDefaultData() {
      try {
        const response = await this.getDefault();
        if (response.success) {
          this.defaultData = response.data;
        } else {
          console.error(response.error);
          this.defaultData = {};
        }
      } catch (error) {
        console.error("Error fetching default settings:", error);
        return {};
      }
    },
    async getCurrency() {
      const response = await this.fetchCurrency();
      if (response.success) {
        this.currencies = response.currency;
      }
    },
    async fetchProducts() {
      const response = await this.getProducts();
      if (response.success) {
        this.products = response.products;
      } else {
        console.error(response.error);
      }
    },
    showSettings() {
      this.showAdSettings = !this.showAdSettings;
    },
    async saveSettings(integrationData) {
      this.saving = true;
      this.saveSuccess = false;

      try {
        console.log("Saving integration data:", integrationData);
        await this.saveFacebookIntegration(integrationData);
        toast.success(
          "Settings saved successfully! The system will use these for automated ad creation."
        );
      } catch (error) {
        console.error("Error saving Facebook integration:", error);
        toast.error(error.message || "Failed to save integration");
      } finally {
        this.saving = false;
      }
    },

    goback() {
      this.$router.go(-1);
    },
  },
};
</script>

<style scoped>
/* in your global CSS (e.g., index.css or app.css) */
@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  10%,
  30%,
  50%,
  70%,
  90% {
    transform: translateX(-5px);
  }
  20%,
  40%,
  60%,
  80% {
    transform: translateX(5px);
  }
}

.shake {
  animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) infinite;
}
</style>
